import {TMRJobStatus, TMRJob} from '@crema/services/apis/tmr';
import {AppActions} from '../../types';
import {
  TMR_UPLOAD_START,
  TMR_UPLOAD_SUCCESS,
  TMR_UPLOAD_FAILURE,
  TMR_POLL_UPDATE,
  TMR_POLL_STOP,
  TMR_FETCH_HISTORY_START,
  TMR_FETCH_HISTORY_SUCCESS,
  TMR_FETCH_HISTORY_FAILURE,
  TMR_CLEAR_CURRENT_JOB,
} from '../../types/actions/TMR.actions';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

interface TMRState {
  /** The job currently being tracked (null until the user submits a file). */
  currentJob: TMRJobStatus | null;
  /** True while the polling interval is expected to be running. */
  isPolling: boolean;
  /** All reduction jobs belonging to the authenticated user. */
  jobHistory: TMRJob[];
  /** True while the upload POST request is in flight. */
  isUploading: boolean;
  /** True while the job-history GET request is in flight. */
  isFetchingHistory: boolean;
  /** Module-level error message (null when there is no error). */
  error: string | null;
}

const INIT_STATE: TMRState = {
  currentJob: null,
  isPolling: false,
  jobHistory: [],
  isUploading: false,
  isFetchingHistory: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const TMR = (state: TMRState = INIT_STATE, action: AppActions): TMRState => {
  switch (action.type) {
    // --- Upload lifecycle ---
    case TMR_UPLOAD_START: {
      return {
        ...state,
        isUploading: true,
        error: null,
      };
    }

    case TMR_UPLOAD_SUCCESS: {
      return {
        ...state,
        isUploading: false,
        isPolling: true,
        // Seed currentJob with the initial status so the polling component
        // renders immediately without waiting for the first poll tick.
        currentJob: {
          job_id: action.payload.jobId,
          status: action.payload.status as TMRJobStatus['status'],
        },
        error: null,
      };
    }

    case TMR_UPLOAD_FAILURE: {
      return {
        ...state,
        isUploading: false,
        error: action.error,
      };
    }

    // --- Polling ---
    case TMR_POLL_UPDATE: {
      const pollable =
        action.payload.status === 'pending' ||
        action.payload.status === 'running';

      return {
        ...state,
        currentJob: action.payload,
        isPolling: pollable,
      };
    }

    case TMR_POLL_STOP: {
      return {
        ...state,
        isPolling: false,
      };
    }

    // --- History ---
    case TMR_FETCH_HISTORY_START: {
      return {
        ...state,
        isFetchingHistory: true,
      };
    }

    case TMR_FETCH_HISTORY_SUCCESS: {
      return {
        ...state,
        isFetchingHistory: false,
        jobHistory: action.payload,
      };
    }

    case TMR_FETCH_HISTORY_FAILURE: {
      return {
        ...state,
        isFetchingHistory: false,
        error: action.error,
      };
    }

    // --- Reset ---
    case TMR_CLEAR_CURRENT_JOB: {
      return {
        ...state,
        currentJob: null,
        isPolling: false,
        error: null,
      };
    }

    default:
      return state;
  }
};

export default TMR;
